import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { Loader, NumberInput, Tooltip } from '../../../components';
import AnimalInfo from '../../../components/animal/AnimalInfo';
import OnFarmAnimalSelector from './OnFarmAnimalSelector';
import BackButton from '../../../components/back-button/BackButton';
import FilterDropdown, { FilterOption } from '../../../components/custom-input/FilterDropdown';
import ValidatedInput, { ValidatedState } from '../../../components/custom-input/ValidatedInput';
import SpecimenLotInfo from '../../../components/specimen-lot-info/SpecimenLotInfo';
import { InfoSVG } from '../../../components/svgs';
import {
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { ON_FARM_PATHS, ROUTE_PATHS } from '../../../constants/routePaths';
import { onFarmSellPost } from '../../../api/onFarmTransactionApi';
import { showToast } from '../../../services/toast.service';
import { downloadOnFarmTransactionExport } from '../../../services/onFarmTransactionExport.service';
import { getOnFarmAnimalById, getOnFarmSpecimensByOwnership } from '../../../services/onFarmInventory.service';
import { SellType } from '../../../types/enums/sellType.enum';
import { Validators } from '../../../types/enums/validators.enum';
import { Genders, InventorySource, TransactionTypeEnum } from '../../../types/enums';
import { Animal, SpecimenInventory, SpecimenLocation } from '../../../types/interfaces';
import { OnFarmSellRequestBody } from '../../../types/interfaces/onFarmTransaction.interfaces';
import { dateFormatMMDDYYYY } from '../../../utils/commonUtils';
import './onFarmInventory.scss';

const initialValidatedState: ValidatedState = { value: '', valid: false };

type SellItem = {
  availableQty: number;
  isSelected: boolean;
  quantity: number;
  specimenId: number;
  embryoStrawId?: number;
  selectable?: boolean;
};

type LocationSellAmount = {
  key: string;
  location: SpecimenLocation;
  amount: number;
};

const OnFarmSellTransfer = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();
  const accountId = useAppSelector(state => state.activeAccount.accountId);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sellingInventory, setSellingInventory] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>('');
  const [ownership, setOwnership] = useState<SpecimenInventory[]>();
  const [selectedSellItems, setSelectedSellItems] = useState<SellItem[]>([]);
  const [locationSellAmounts, setLocationSellAmounts] = useState<LocationSellAmount[]>([]);
  const [totalAvailableOwnedQty, setTotalAvailableOwnedQty] = useState<number>();
  const [animal, setAnimal] = useState<Animal>();
  const [embryoMissingSpecimenIds, setEmbryoMissingSpecimenIds] = useState<number[]>([]);
  const [sellType, setSellType] = useState<FilterOption<SellType>>({ name: SellType.Sell.valueOf(), value: SellType.Sell });
  const [buyerName, setBuyerName] = useState<ValidatedState>(initialValidatedState);
  const [buyerEmail, setBuyerEmail] = useState<ValidatedState>(initialValidatedState);
  const [buyerPhone, setBuyerPhone] = useState<ValidatedState>(initialValidatedState);
  const [validate, setValidate] = useState<boolean>(false);

  useEffect(() => {
    if (animalId) {
      fetchAnimal();
    } else {
      setAnimal(undefined);
    }
  }, [animalId]);

  const fetchAnimal = async () => {
    try {
      const response = await getOnFarmAnimalById(animalId ?? '', { include: 'Breed.Species' });
      setAnimal(response.data);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const isFemale = animal?.gender === Genders.Female;

  const getSpecimenKey = useCallback(
    (specimen: SpecimenInventory) => {
      if (isFemale && specimen.embryoStrawId) {
        return `embryo-${specimen.embryoStrawId}`;
      }
      return `specimen-${specimen.specimenId}`;
    },
    [isFemale],
  );

  const selectedSpecimens = (ownership ?? [])
    .map((specimen, index) => ({
      specimen,
      sellItem: selectedSellItems[index],
      key: getSpecimenKey(specimen),
      index,
    }))
    .filter(row => row.sellItem?.isSelected);

  const totalSellQuantity = selectedSellItems
    .filter(item => item.isSelected)
    .reduce((accumulator, current) => accumulator + current.quantity, 0);

  const totalLocationSellAmounts = locationSellAmounts.reduce((total, locationAmount) => {
    return total + (locationAmount.amount || 0);
  }, 0);

  useEffect(() => {
    const handleGetSpecimens = async () => {
      if (animalId && accountId && animal) {
        try {
          const { data } = await getOnFarmSpecimensByOwnership(animalId, accountId);

          let missingEmbryoSpecimenIds: number[] = [];
          if (animal?.gender === Genders.Female) {
            missingEmbryoSpecimenIds = data
              .filter(spec => spec.quantityAvailable > 0 && spec.embryoStraws?.length === 0)
              .map(spec => spec.specimenId);
          }
          setEmbryoMissingSpecimenIds(missingEmbryoSpecimenIds);

          let flattenedOwnership: SpecimenInventory[] = [];
          if (animal?.gender === Genders.Female) {
            flattenedOwnership = data.flatMap((specimen: SpecimenInventory) => {
              if (specimen.embryoStraws && specimen.embryoStraws.length > 0) {
                return specimen.embryoStraws.map(straw => ({
                  ...specimen,
                  embryoStrawCode: straw.embryoStrawCode,
                  embryoStrawNumber: straw.embryoStrawNumber,
                  embryoStrawId: straw.embryoStrawId,
                  quantityAvailable: straw.embryoCountPerStraw ?? 0,
                  embryoGrade: straw.embryoGrade,
                  embryoStage: straw.embryoStage,
                  specimenLocations: (specimen.specimenLocations ?? []).filter(
                    location => !straw.storageCanisterId || location.storageCanisterId === straw.storageCanisterId,
                  ),
                }));
              }
              return specimen;
            });
          } else {
            flattenedOwnership = data;
          }

          flattenedOwnership = flattenedOwnership.filter(item => (item.quantityAvailable ?? 0) > 0);

          setOwnership(flattenedOwnership);
          setTotalAvailableOwnedQty(flattenedOwnership.reduce((acc, curr) => acc + curr.quantityAvailable, 0));
          handleCreateSellItems(flattenedOwnership, animal?.gender === Genders.Female);
          setLocationSellAmounts([]);
          setIsLoading(false);
        } catch (error) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };

    handleGetSpecimens();
  }, [animalId, accountId, animal]);

  useEffect(() => {
    selectedSellItems.forEach(inventoryLot => {
      if (inventoryLot.isSelected && inventoryLot.quantity > inventoryLot.availableQty) {
        showToast.error(toastMessages.CANT_SELL_MORE_THAN_AVAIL_QTY);
      }
    });
  }, [selectedSellItems]);

  useEffect(() => {
    setLocationSellAmounts(previousLocations => {
      const updatedLocations = [...previousLocations];

      selectedSpecimens.forEach(({ specimen, key: specimenKey, sellItem }) => {
        specimen.specimenLocations?.forEach(specimenLocation => {
          const alreadyExists = updatedLocations.some(
            existingLocation =>
              existingLocation.key === specimenKey &&
              existingLocation.location.storageCanisterId === specimenLocation.storageCanisterId,
          );

          if (!alreadyExists) {
            updatedLocations.push({
              key: specimenKey,
              location: specimenLocation,
              amount: isFemale ? sellItem?.availableQty ?? specimen.quantityAvailable ?? 0 : 0,
            });
          }
        });
      });

      return updatedLocations.filter(existingLocation =>
        selectedSpecimens.some(selectedSpecimen => selectedSpecimen.key === existingLocation.key),
      );
    });
  }, [selectedSellItems, ownership, isFemale]);

  const handleCreateSellItems = (ownershipItems: SpecimenInventory[], female: boolean) => {
    const sellItems = ownershipItems.map(item => {
      return {
        isSelected: false,
        specimenId: item.specimenId,
        embryoStrawId: item.embryoStrawId,
        quantity: 0,
        availableQty: item.quantityAvailable,
        selectable: female ? !!item.embryoStrawId : true,
      };
    });
    setSelectedSellItems(sellItems);
  };

  const isSemenLocationQuantityValid = () => {
    return selectedSpecimens.every(({ sellItem, key }) => {
      if (isFemale || !sellItem) {
        return true;
      }

      const locationTotal = locationSellAmounts
        .filter(locationAmount => locationAmount.key === key)
        .reduce((sum, locationAmount) => sum + (locationAmount.amount || 0), 0);

      return sellItem.quantity === locationTotal;
    });
  };

  const buyerInfoValid = () => buyerName.valid && buyerEmail.valid && buyerPhone.valid;

  const isValid = (isSubmit?: boolean) => {
    if (!selectedSellItems.find(item => item.isSelected)) {
      isSubmit && showToast.error(toastMessages.NO_ITEM_SELECTED);
      return false;
    } else if (selectedSellItems.some(item => item.isSelected && item.quantity > item.availableQty)) {
      isSubmit && showToast.error(toastMessages.CANT_SELL_MORE_THAN_AVAIL_QTY);
      return false;
    } else if (!totalAvailableOwnedQty || totalSellQuantity > totalAvailableOwnedQty) {
      isSubmit && showToast.error(toastMessages.CANT_SELL_MORE_THAN_AVAIL_QTY);
      return false;
    } else if (totalSellQuantity === 0 || selectedSellItems.some(item => item.isSelected && item.quantity === 0)) {
      isSubmit && showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return false;
    } else if (
      locationSellAmounts.length === 0 ||
      totalLocationSellAmounts !== totalSellQuantity ||
      locationSellAmounts.some(location => location.amount > location.location.quantity) ||
      !isSemenLocationQuantityValid()
    ) {
      isSubmit && showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
      return false;
    } else if (!buyerInfoValid()) {
      isSubmit && showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
      return false;
    }
    return true;
  };

  const buildSellNotes = (): string => {
    const noteParts = [
      notes.trim() || undefined,
      `Sell Type: [${sellType.value}]`,
      `Buyer: ${buyerName.value}`,
      `Email: ${buyerEmail.value}`,
      buyerPhone.value ? `Phone: ${buyerPhone.value}` : undefined,
    ].filter(Boolean);

    return noteParts.join(' - ');
  };

  const buildSellRequests = (): OnFarmSellRequestBody[] => {
    const sellGroups = new Map<string, OnFarmSellRequestBody>();
    const sellNotes = buildSellNotes();

    locationSellAmounts
      .filter(locationAmount => locationAmount.amount > 0)
      .forEach(locationAmount => {
        const selected = selectedSpecimens.find(row => row.key === locationAmount.key);
        const specimenId = selected?.sellItem.specimenId ?? locationAmount.location.specimenId!;
        const accountStorageCanisterId = locationAmount.location.storageCanisterId;
        const key = `${specimenId}-${accountStorageCanisterId}`;
        const existing = sellGroups.get(key);

        if (existing) {
          existing.quantityToSell += locationAmount.amount;
          if (isFemale && selected?.sellItem.embryoStrawId) {
            existing.selectedEmbryoStrawIds = [...(existing.selectedEmbryoStrawIds ?? []), selected.sellItem.embryoStrawId];
          }
        } else {
          sellGroups.set(key, {
            onFarmSpecimenId: specimenId,
            accountStorageCanisterId,
            quantityToSell: locationAmount.amount,
            notes: sellNotes,
            ...(isFemale && selected?.sellItem.embryoStrawId
              ? { selectedEmbryoStrawIds: [selected.sellItem.embryoStrawId] }
              : {}),
          });
        }
      });

    return Array.from(sellGroups.values());
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidate(true);

    if (!sellingInventory) return;

    const hasQuantityMismatch =
      (totalSellQuantity > 0 || totalLocationSellAmounts > 0) && totalLocationSellAmounts !== totalSellQuantity;
    const hasSemenMismatch = !isSemenLocationQuantityValid();

    if (!isValid(true)) {
      if (hasQuantityMismatch || hasSemenMismatch) {
        showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
      }
      return;
    }

    setSellingInventory(false);
    try {
      setIsLoading(true);
      const sellRequests = buildSellRequests();

      for (const sellRequest of sellRequests) {
        const { data: onFarmTransactionId } = await onFarmSellPost(sellRequest);
        await downloadOnFarmTransactionExport(
          {
            onFarmTransactionId,
            sellDetails: sellRequest,
          },
          TransactionTypeEnum.Sell,
        );
      }

      showToast.success(toastMessages.SALE_HAS_BEEN_SUBMITTED);
      navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY);
    } catch (error) {
      showToast.error(toastMessages.FAILED_TO_SUBMIT_SALE);
    } finally {
      setSellingInventory(true);
      setIsLoading(false);
    }
  };

  const inputCheckbox = (sellItem: SellItem | undefined, index: number) => {
    return (
      <input
        type="checkbox"
        name="selected-sell-specimen-row"
        disabled={sellItem?.selectable === false}
        onChange={e =>
          setSelectedSellItems(prev => {
            const newItems = [...prev];
            if (sellItem) {
              const updatedItem = { ...sellItem, isSelected: e.target.checked };
              if (isFemale) {
                updatedItem.quantity = e.target.checked ? sellItem.availableQty : 0;
              } else if (!e.target.checked) {
                updatedItem.quantity = 0;
              }

              newItems[index] = updatedItem;
            }
            return newItems;
          })
        }
        checked={sellItem?.isSelected || false}
      />
    );
  };

  const inputQuantityElement = (sellItem: SellItem | undefined, index: number) => {
    const displayValue = isFemale
      ? sellItem?.isSelected
        ? sellItem?.availableQty ?? 0
        : '-'
      : sellItem?.isSelected
      ? sellItem.quantity > 0
        ? sellItem.quantity
        : ''
      : '-';

    return (
      <input
        type="text"
        placeholder="0"
        value={displayValue}
        disabled={isFemale || !sellItem?.isSelected}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (isFemale) return;
          const count = +e.target.value.replace(/[^\d]+/, '').replace(/^0+/, '');
          let newQuantities = [...selectedSellItems];
          newQuantities[index] = { ...selectedSellItems[index], quantity: count };
          setSelectedSellItems(newQuantities);
        }}
      />
    );
  };

  const handleErrors = (sellItem: SellItem | undefined, item: SpecimenInventory) => {
    return sellItem?.isSelected
      ? (sellItem?.quantity ?? 0) > item?.quantityAvailable
        ? 'error'
        : sellItem?.quantity === 0
        ? 'warning'
        : ''
      : '';
  };

  const handleLocationRowClass = useCallback(
    (location: SpecimenLocation, key: string): string => {
      const current = locationSellAmounts.find(
        locationAmount => locationAmount.key === key && locationAmount.location.storageCanisterId === location.storageCanisterId,
      );
      if (current && current?.amount > location.quantity) {
        showToast.error(toastMessages.CHOSE_MORE_THAN_TOTAL);
        return 'specimen-row invalid-row';
      }
      return 'specimen-row';
    },
    [locationSellAmounts],
  );

  const handleSpecimenLocationAmountChange = (key: string, canisterId: number, newAmount: number) => {
    setLocationSellAmounts(prev =>
      prev.map(location =>
        location.key === key && location.location.storageCanisterId === canisterId
          ? { ...location, amount: newAmount }
          : location,
      ),
    );
  };

  const renderLocationTableRows = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const rowClass = `${handleLocationRowClass(location, key)} ${isTravelingTank ? 'disabled' : ''}`;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name}/${location.storageCanister?.storageTank?.name}/${location.storageCanister?.name}`;
    const animalDetails = `${specimen.animal?.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationSellAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <tr className={rowClass} key={`${key}-loc-${location?.storageCanisterId}`}>
        <td>{locationPath}</td>
        <td>{animalDetails}</td>
        <td>{location.quantity}</td>
        {isFemale && <td>{specimen.embryoStrawCode || '-'}</td>}
        <td>
          {isTravelingTank ? (
            LABEL_CONSTANTS.TRAVELING_TANK
          ) : isFemale ? (
            <NumberInput value={specimen.quantityAvailable} />
          ) : (
            <NumberInput
              onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
              value={currentAmount.toString()}
            />
          )}
        </td>
      </tr>
    );
  };

  const renderLocationMobileRow = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name} / ${location.storageCanister?.storageTank?.name} / ${location.storageCanister?.name}`;
    const animalDetails = `${specimen.animal?.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationSellAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <div className="selected-storage-location" key={`${key}-loc-${location.storageCanisterId}`}>
        <div className={`card max-width ${isTravelingTank ? 'disabled' : ''}`}>
          <div className="body">
            <div className="body-content">
              <div className="selected-location">
                <label>{TABLE_HEADER_CONSTANTS.LOCATION}:</label>
                <label>{locationPath}</label>
              </div>
              <div className="location-item">
                <label>{TABLE_HEADER_CONSTANTS.ITEM}:</label>
                <label>{animalDetails}</label>
              </div>
            </div>
            <hr />
            <div className="body-content">
              <div className="canister-qty">
                <label>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</label>
                <label>{location.quantity}</label>
              </div>
              <div className="actual-quantity">
                {isTravelingTank ? (
                  <label>{LABEL_CONSTANTS.TRAVELING_TANK}</label>
                ) : isFemale ? (
                  <>
                    <label>{TABLE_HEADER_CONSTANTS.QUANTITY}</label>
                    <NumberInput value={specimen.quantityAvailable} />
                  </>
                ) : (
                  <>
                    <label>{TABLE_HEADER_CONSTANTS.QUANTITY}</label>
                    <NumberInput
                      onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
                      value={currentAmount.toString()}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="on-farm on-farm-use inventory-sell">
      <BackButton />
      <div className="inventory-action card">
        <h1>{PAGE_HEADER_CONSTANTS.SELL_INVENTORY}</h1>
        <OnFarmAnimalSelector animalId={animalId} actionPath={ON_FARM_PATHS.SELL_TRANSFER} />
        {isLoading ? (
          <Loader loaderSize="medium" simple />
        ) : (
          <form action="submit" onSubmit={e => handleSubmit(e)}>
            <AnimalInfo animalId={animalId} showQuantityAvailable inventorySource={InventorySource.ON_FARM} />
            <br />

            <h2>{LABEL_CONSTANTS.SELECT_ITEM_QUANTITY}</h2>
            <div className="desk-select-specimens">
              <div className="specimen-table">
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                      <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                      <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                      <th>{TABLE_HEADER_CONSTANTS.LOT_NO}</th>
                      {isFemale ? (
                        <>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                          <th>{TABLE_HEADER_CONSTANTS.FLUSH_MATING}</th>
                        </>
                      ) : (
                        <th>{TABLE_HEADER_CONSTANTS.QUALITY}</th>
                      )}
                      <th>{isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                      <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownership?.map((item: SpecimenInventory, index: number) => {
                      const sellItem = selectedSellItems.find(selectedItem =>
                        isFemale
                          ? selectedItem.embryoStrawId === item.embryoStrawId
                          : selectedItem.specimenId === item.specimenId,
                      );
                      const flushMating: string = String(
                        item.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')
                          ?.value ?? '-',
                      );

                      return (
                        <tr key={index} className={handleErrors(sellItem, item)}>
                          <td>{inputCheckbox(sellItem, index)}</td>
                          <td>
                            {item.animal?.code}-{item.animal?.name}
                          </td>
                          <td>{item.specimenType?.name}</td>
                          <td>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</td>
                          {isFemale ? (
                            <>
                              <td>
                                <div>
                                  {item.embryoStrawCode ?? '-'}
                                  <Tooltip>
                                    {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`} <br />
                                    {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                  </Tooltip>
                                </div>
                              </td>
                              <td>{item.embryoStrawNumber ?? '-'}</td>
                              <td>{flushMating}</td>
                            </>
                          ) : (
                            <td>{item.quality}</td>
                          )}

                          <td>{item.quantityAvailable}</td>
                          <td>{inputQuantityElement(sellItem, index)}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan={isFemale ? 8 : 6}></td>
                      <td colSpan={3}>
                        <div className="quantity">{totalSellQuantity}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="xs-select-specimens">
              {ownership &&
                ownership.map((item, index) => {
                  const sellItem = selectedSellItems.find(selectedItem =>
                    isFemale ? selectedItem.embryoStrawId === item.embryoStrawId : selectedItem.specimenId === item.specimenId,
                  );
                  const flushMating: string = String(
                    item.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')?.value ??
                      '-',
                  );

                  return (
                    <div
                      className={`card max-width ${handleErrors(sellItem, item)}`}
                      key={`specimen-row:${item.specimenId}-straw:${item.embryoStrawId ?? 'none'}-index:${index}`}>
                      <div className="selected-specimens-row">
                        <div className="input-check">{inputCheckbox(sellItem, index)}</div>
                        <div className="select-specimens">
                          <div className="card-content-section">
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                              <label>{`${item.animal?.code} - ${item.animal?.name}`}</label>
                            </div>
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                              <label>{`${item.specimenType?.name}`}</label>
                            </div>
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.LOT_NO}: `}</label>
                              <label>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</label>
                            </div>
                            <div className="right column">
                              <label>{`${TABLE_HEADER_CONSTANTS.FLUSH_MATING}: `}</label>
                              <label>{flushMating}</label>
                            </div>
                          </div>
                          <div className="card-content-section">
                            {isFemale ? (
                              <>
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                                  <label>
                                    {`${item.embryoStrawCode ?? '-'}`}{' '}
                                    {isFemale && (
                                      <Tooltip>
                                        {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`} <br />
                                        {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                      </Tooltip>
                                    )}
                                  </label>
                                </div>
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.STRAW_NUMBER}: `}</label>
                                  <label>{`${item.embryoStrawNumber ?? '-'}`}</label>
                                </div>
                              </>
                            ) : (
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.QUALITY}: `}</label>
                                <label>{`${item.quality}`}</label>
                              </div>
                            )}
                            <div className="left column ">
                              <label>
                                {`${isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}
                              </label>
                              <label>{`${item.quantityAvailable}`}</label>
                            </div>
                            <div className="right center column">
                              <label>{`${TABLE_HEADER_CONSTANTS.QUANTITY}:`}</label>
                              {inputQuantityElement(sellItem, index)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              <div className="total-quantity">
                <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}: &nbsp; </label>
                {totalSellQuantity}
              </div>
            </div>
            <br />

            <h2>{LABEL_CONSTANTS.ITEM_STORAGE_LOCATIONS}</h2>
            <div className="use-storage-locations">
              <div className="desk-storage-locations-table">
                <div className="specimen-table">
                  <table>
                    <thead>
                      <tr>
                        <th>{TABLE_HEADER_CONSTANTS.LOCATION}</th>
                        <th>{TABLE_HEADER_CONSTANTS.ITEM}</th>
                        <th>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</th>
                        {isFemale && <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>}
                        <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSpecimens.length === 0 ? (
                        <>
                          <tr>
                            <td colSpan={isFemale ? 5 : 4} className="storage-locations-placeholder">
                              {LABEL_CONSTANTS.SELECT_SPECIMEN_TO_VIEW_STORAGE_LOCATIONS}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={isFemale ? 4 : 3}></td>
                            <td colSpan={1}>
                              <div className="quantity storage-total">0</div>
                            </td>
                          </tr>
                        </>
                      ) : (
                        <>
                          {selectedSpecimens.map(({ specimen, key }) =>
                            specimen.specimenLocations?.map(location => renderLocationTableRows(specimen, key, location)),
                          )}
                          <tr className={totalLocationSellAmounts > totalSellQuantity ? 'invalid-row' : ''}>
                            <td colSpan={isFemale ? 4 : 3}></td>
                            <td colSpan={1}>
                              <div className="quantity storage-total">{totalLocationSellAmounts}</div>
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="xs-storage-locations-table">
                {selectedSpecimens.length === 0 ? (
                  <div className="storage-locations-empty-card">
                    <p>{LABEL_CONSTANTS.SELECT_SPECIMEN_TO_VIEW_STORAGE_LOCATIONS}</p>
                  </div>
                ) : (
                  <>
                    {selectedSpecimens.map(({ specimen, key }) =>
                      specimen.specimenLocations?.map(location => renderLocationMobileRow(specimen, key, location)),
                    )}
                  </>
                )}
                <div className="storage-total">
                  <label>{LABEL_CONSTANTS.SELECTED_QTY}:&nbsp;</label>
                  {totalLocationSellAmounts}
                </div>
              </div>
            </div>
            <br />

            <h2>{LABEL_CONSTANTS.BUYER_INFO}</h2>
            <div className="form-row">
              <ValidatedInput
                useId="buyer-name"
                label="Buyer Name:"
                type="text"
                setValidatedState={setBuyerName}
                validate={validate}
                validatedStateForAutoFill={buyerName}
                validators={[Validators.REQUIRED]}
              />
            </div>
            <div className="form-row">
              <ValidatedInput
                useId="buyer-email"
                label={LABEL_CONSTANTS.BUYER_EMAIL}
                type="email"
                setValidatedState={setBuyerEmail}
                validate={validate}
                validatedStateForAutoFill={buyerEmail}
                validators={[Validators.REQUIRED, Validators.EMAIL]}
              />
            </div>
            <div className="form-row">
              <ValidatedInput
                useId="buyer-phone"
                label={LABEL_CONSTANTS.BUYER_PHONE_NUMBER}
                type="phone"
                setValidatedState={setBuyerPhone}
                validate={validate}
                validatedStateForAutoFill={buyerPhone}
                validators={[]}
              />
            </div>

            {!sellingInventory && <Loader loaderSize={'small'} simple />}

            <br />
            <div className="form-row">
              <label>{LABEL_CONSTANTS.SELL_TYPE}:</label>
              <div className="input-container">
                <FilterDropdown
                  options={[
                    { name: SellType.Sell, value: SellType.Sell },
                    { name: SellType.Transfer, value: SellType.Transfer },
                    { name: SellType.Donate, value: SellType.Donate },
                    { name: SellType.Other, value: SellType.Other },
                  ]}
                  placeholder="Choose Type"
                  value={sellType}
                  onChange={e => setSellType(e)}
                />
              </div>
            </div>
            <div className="form-row expanded-text-area">
              <label htmlFor="on-farm-sell-notes">{LABEL_CONSTANTS.NOTES}</label>
              <textarea
                id="on-farm-sell-notes"
                name="notes"
                placeholder={LABEL_CONSTANTS.NOTES}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={4}
                cols={50}
              />
            </div>
            <br />
            <div className="submit-button">
              <button className={`button green small ${!isValid() && 'disabled'}`} type="submit">
                {BUTTON_CONSTANTS.SUBMIT}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OnFarmSellTransfer;
